import { create } from "zustand";
import { SettingStoreProps } from "./interface/setting.interface";
import {
  createSettingCombustion,
  createSettingFlareCompound,
  createSettingFlareInfo,
  createSettingWasteWater,
  deleteSettingCombustion,
  deleteSettingFlareCompound,
  deleteSettingFlareInfo,
  deleteSettingWasteWater,
  getFilterCombustionColumn,
  getFilterFlareCompoundColumn,
  getFilterFlareInfoColumn,
  getFilterWasteWaterColumn,
  getMasterData,
  searchSettingCombustion,
  searchSettingFlareCompound,
  searchSettingFlareInfo,
  searchSettingWasteWater,
  updateSettingCombustion,
  updateSettingFlareCompound,
  updateSettingFlareInfo,
  updateSettingWasteWater,
} from "src/services/settings.service";
import { CreateSettingCombustion, FilterSettingCombustion, UpdateSettingCombustion } from "src/entity/settings/combustion";
import { FilterMasterDataProps } from "src/entity/settings/masterdata";
import { CreateSettingFlareInfo, FilterSettingFlareInfo, UpdateSettingFlareInfo } from "src/entity/settings/flare-info";
import { CreateSettingFlareCompound, FilterSettingFlareCompound, UpdateSettingFlareCompound } from "src/entity/settings/flare-compound";
import {
  CreateSettingWasteWaterTreatment,
  FilterSettingWasteWaterTreatment,
  UpdateSettingWasteWaterTreatment,
} from "src/entity/settings/waste-water";

export const settingStore = create<SettingStoreProps>((set) => ({
  loading: false,
  data: [],
  total: 0,
  error: null,
  fuelType: [],
  emission: [],
  unit: [],
  filterFlareInfo: {
    flareId: [],
    flareName: [],
  },
  filterFlareCompound: {
    compound: [],
    kcalM3: [],
    kcalKg: [],
    remark: [],
  },
  filterCombustion: {
    emissionFactor: [],
    remark: [],
  },
  loadingGetMaster: false,
  errorGetMaster: null,
  errorManaging: null,
  loadingManageCombustion: false,
  // Flare Info
  dataSettingFlareInfo: { data: [], total: 0 },
  errorManageFlareInfo: null,
  loadingGetFlareInfo: false,
  loadingManageFlareInfo: false,
  // Flare Compound
  dataSettingFlareCompound: { data: [], total: 0 },
  errorManageFlareCompound: null,
  loadingGetFlareCompound: false,
  loadingManageFlareCompound: false,
  // Flare Compound
  dataSettingWasteWater: { data: [], total: 0 },
  loadingManageWasteWater: false,
  loadingGetWasteWater: false,
  errorManageWasteWater: null,
  filterWasteWater: {
    emissionControl: [],
    efficiency: [],
  },
  getSettingCombustion: async (filter: FilterSettingCombustion) => {
    set(() => ({ loading: true }));
    try {
      const res = await searchSettingCombustion(filter);
      set(() => ({ loading: false, data: res.data?.data?.data || [], total: res.data.data.total || 0 }));
    } catch (err: any) {
      set(() => ({ loading: false, error: err?.message, data: [] }));
    }
  },
  getFuelType: async (filter: FilterMasterDataProps) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getMasterData(filter);
      set(() => ({ loadingGetMaster: false, fuelType: res.data?.data || [] }));
    } catch (err: any) {
      set(() => ({ loadingGetMaster: false, errorGetMaster: err?.message, fuelType: [] }));
    }
  },
  getEmission: async (filter: FilterMasterDataProps) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getMasterData(filter);
      set(() => ({ loadingGetMaster: false, emission: res.data?.data || [] }));
    } catch (err: any) {
      set(() => ({ loadingGetMaster: false, errorGetMaster: err?.message, emission: [] }));
    }
  },
  getUnit: async (filter: FilterMasterDataProps) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getMasterData(filter);
      set(() => ({ loadingGetMaster: false, unit: res.data?.data || [] }));
    } catch (err: any) {
      set(() => ({ loadingGetMaster: false, errorGetMaster: err?.message, unit: [] }));
    }
  },
  getFilterCombustionColumn: async (type: string) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getFilterCombustionColumn(type);
      set((state) => ({
        loadingGetMaster: false,
        filterCombustion: {
          ...state.filterCombustion,
          [type]: res.data?.data || [],
        },
      }));
    } catch (err: any) {
      set((state) => ({
        loadingGetMaster: false,
        errorGetMaster: err?.message,
        filterCombustion: {
          ...state.filterCombustion,
          [type]: [],
        },
      }));
    }
  },
  getFilterColumn: async (type: string) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getFilterFlareInfoColumn(type);
      set((state) => ({
        loadingGetMaster: false,
        filterFlareInfo: {
          ...state.filterFlareInfo,
          [type]: res.data?.data || [],
        },
      }));
    } catch (err: any) {
      set((state) => ({
        loadingGetMaster: false,
        errorGetMaster: err?.message,
        filterFlareInfo: {
          ...state.filterFlareInfo,
          [type]: [],
        },
      }));
    }
  },
  getFilterCompoundColumn: async (type: string) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getFilterFlareCompoundColumn(type);
      set((state) => ({
        loadingGetMaster: false,
        filterFlareCompound: {
          ...state.filterFlareCompound,
          [type]: res.data?.data || [],
        },
      }));
    } catch (err: any) {
      set((state) => ({
        loadingGetMaster: false,
        errorGetMaster: err?.message,
        filterFlareCompound: {
          ...state.filterFlareCompound,
          [type]: [],
        },
      }));
    }
  },
  createSettingCombustion: async (body: CreateSettingCombustion) => {
    set(() => ({ loadingManageCombustion: true }));
    try {
      await createSettingCombustion(body);
      set(() => ({ loadingManageCombustion: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageCombustion: false, errorManaging: message }));
      return message;
    }
  },
  updateSettingCombustion: async (body: UpdateSettingCombustion) => {
    set(() => ({ loadingManageCombustion: true }));
    try {
      await updateSettingCombustion(body);
      set(() => ({ loadingManageCombustion: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageCombustion: false, errorManaging: message }));
      return message;
    }
  },
  deleteSettingCombustion: async (id: string) => {
    set(() => ({ loadingManageCombustion: true }));
    try {
      const res = await deleteSettingCombustion(id);
      set(() => ({ loadingManageCombustion: false }));
    } catch (err: any) {
      set(() => ({ loadingManageCombustion: false, errorManaging: err?.message }));
    }
  },
  clearErrorSettingCombustion: () => {
    set(() => ({ errorManaging: null }));
  },
  // Flare Info
  getSettingFlareInfo: async (filter: FilterSettingFlareInfo) => {
    set(() => ({ loadingGetFlareInfo: true }));
    try {
      const res = await searchSettingFlareInfo(filter);
      set(() => ({ loadingGetFlareInfo: false, dataSettingFlareInfo: res.data?.data }));
    } catch (err: any) {
      set(() => ({ loadingGetFlareInfo: false, error: err?.message, dataSettingFlareInfo: { data: [], total: 0 } }));
    }
  },
  createSettingFlareInfo: async (body: CreateSettingFlareInfo) => {
    set(() => ({ loadingManageFlareInfo: true }));
    try {
      await createSettingFlareInfo(body);
      set(() => ({ loadingManageFlareInfo: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageFlareInfo: false, errorManageFlareInfo: message }));
      return message;
    }
  },
  updateSettingFlareInfo: async (body: UpdateSettingFlareInfo) => {
    set(() => ({ loadingManageFlareInfo: true }));
    try {
      await updateSettingFlareInfo(body);
      set(() => ({ loadingManageFlareInfo: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageFlareInfo: false, errorManageFlareInfo: message }));
      return message;
    }
  },
  deleteSettingFlareInfo: async (id: string) => {
    set(() => ({ loadingManageFlareInfo: true }));
    try {
      const res = await deleteSettingFlareInfo(id);
      set(() => ({ loadingManageFlareInfo: false }));
    } catch (err: any) {
      set(() => ({ loadingManageFlareInfo: false, errorManageFlareInfo: err?.message }));
    }
  },
  clearErrorSettingFlareInfo: () => {
    set(() => ({ errorManageFlareInfo: null }));
  },
  // Flare Compound
  getSettingFlareCompound: async (filter: FilterSettingFlareCompound) => {
    set(() => ({ loadingGetFlareCompound: true }));
    try {
      const res = await searchSettingFlareCompound(filter);
      set(() => ({ loadingGetFlareCompound: false, dataSettingFlareCompound: res.data?.data }));
    } catch (err: any) {
      set(() => ({
        loadingGetFlareCompound: false,
        errorManageWasteWater: err?.message,
        dataSettingFlareCompound: { data: [], total: 0 },
      }));
    }
  },
  createSettingFlareCompound: async (body: CreateSettingFlareCompound) => {
    set(() => ({ loadingManageFlareCompound: true }));
    try {
      await createSettingFlareCompound(body);
      set(() => ({ loadingManageFlareCompound: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageFlareCompound: false, errorManageFlareCompound: message }));
      return message;
    }
  },
  updateSettingFlareCompound: async (body: UpdateSettingFlareCompound) => {
    set(() => ({ loadingManageFlareCompound: true }));
    try {
      await updateSettingFlareCompound(body);
      set(() => ({ loadingManageFlareCompound: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageFlareCompound: false, errorManageFlareCompound: message }));
      return message;
    }
  },
  deleteSettingFlareCompound: async (id: string) => {
    set(() => ({ loadingManageFlareCompound: true }));
    try {
      const res = await deleteSettingFlareCompound(id);
      set(() => ({ loadingManageFlareCompound: false }));
    } catch (err: any) {
      set(() => ({ loadingManageFlareCompound: false, errorManageFlareCompound: err?.message }));
    }
  },
  clearErrorSettingFlareCompound: () => {
    set(() => ({ errorManageFlareCompound: null }));
  },
  // Setting Waste Water Treatment
  getSettingWasteWater: async (filter: FilterSettingWasteWaterTreatment) => {
    set(() => ({ loadingGetWasteWater: true }));
    try {
      const res = await searchSettingWasteWater(filter);
      set(() => ({ loadingGetWasteWater: false, dataSettingWasteWater: res.data?.data }));
    } catch (err: any) {
      set(() => ({ loadingGetWasteWater: false, errorManageWasteWater: err?.message, dataSettingWasteWater: { data: [], total: 0 } }));
    }
  },
  createSettingWasteWater: async (body: CreateSettingWasteWaterTreatment) => {
    set(() => ({ loadingManageWasteWater: true }));
    try {
      await createSettingWasteWater(body);
      set(() => ({ loadingManageWasteWater: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageWasteWater: false, errorManageWasteWater: message }));
      return message;
    }
  },
  updateSettingWasteWater: async (body: UpdateSettingWasteWaterTreatment) => {
    set(() => ({ loadingManageWasteWater: true }));
    try {
      await updateSettingWasteWater(body);
      set(() => ({ loadingManageWasteWater: false }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ loadingManageWasteWater: false, errorManageWasteWater: message }));
      return message;
    }
  },
  deleteSettingWasteWater: async (id: string) => {
    set(() => ({ loadingManageWasteWater: true }));
    try {
      await deleteSettingWasteWater(id);
      set(() => ({ loadingManageWasteWater: false }));
    } catch (err: any) {
      set(() => ({ loadingManageWasteWater: false, errorManageWasteWater: err?.message }));
    }
  },
  clearErrorSettingWasteWater: () => {
    set(() => ({ errorManageWasteWater: null }));
  },
  getFilterWasteWaterColumn: async () => {
    set(() => ({ loadingGetWasteWater: true }));
    try {
      const res = await getFilterWasteWaterColumn();
      set(() => ({ loadingGetWasteWater: false, filterWasteWater: res.data?.data || [] }));
    } catch (err: any) {
      set(() => ({ loadingGetWasteWater: false, errorManageWasteWater: err?.message }));
    }
  },
}));
